import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ApiEnvironment = 'sandbox' | 'production';
const STORAGE_KEY = 'latlens-api-environment';

interface EnvironmentContextType {
  environment: ApiEnvironment;
  setEnvironment: (env: ApiEnvironment) => void;
  toggle: () => void;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

export const EnvironmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [environment, setEnvironmentState] = useState<ApiEnvironment>(() => {
    if (typeof window === 'undefined') return 'sandbox';
    return (localStorage.getItem(STORAGE_KEY) as ApiEnvironment | null) ?? 'sandbox';
  });

  const setEnvironment = useCallback((env: ApiEnvironment) => {
    localStorage.setItem(STORAGE_KEY, env);
    setEnvironmentState(env);
  }, []);

  const toggle = useCallback(() => {
    setEnvironment(environment === 'sandbox' ? 'production' : 'sandbox');
  }, [environment, setEnvironment]);

  return (
    <EnvironmentContext.Provider value={{ environment, setEnvironment, toggle }}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export function useEnvironment(): EnvironmentContextType {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) throw new Error('useEnvironment must be used within EnvironmentProvider');
  return ctx;
}
